import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, ArrowUpIcon, ArrowRightIcon, ArrowLeftIcon, MicIcon, ImageIcon, PaperclipIcon, PlusIcon, CheckCircle2Icon, ShieldCheckIcon, TruckIcon, LifeBuoyIcon, ClockIcon, AwardIcon, WalletIcon, MapPinIcon } from 'lucide-react';
import { ShopTopBar } from './ShopTopBar';
import { ShopAccountControl, ShopSignInSheet, ShopMyOrders, ShopSession } from './ShopAccount';
import { shopperOrders } from '../../data/orders';
import { ShopBackdrop } from './ShopBackdrop';
import { ShopCategoryStrip } from './ShopCategoryStrip';
import { FeaturedCarCard } from './FeaturedCarCard';
import { CATEGORY_ICONS } from './shopIcons';
import { Vehicle } from '../../data/easylease';
import { ShopConfig } from '../../data/shopConfig';
import { themeCssVars, resolveTheme } from '../../utils/theme';
import { storefrontCategories, storefrontFeatured, splitHeadline } from '../../utils/storefront';
const HIGHLIGHT_ICONS: Record<string, React.ElementType> = {
  shield: ShieldCheckIcon,
  truck: TruckIcon,
  support: LifeBuoyIcon,
  clock: ClockIcon,
  award: AwardIcon,
  wallet: WalletIcon,
  map: MapPinIcon,
  sparkles: SparklesIcon
};
const QUICK_INTENTS = ['Find', 'Rent', 'Compare', 'Availability', 'Delivery'];
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  vehicle?: Vehicle;
};
interface MerchantStorefrontProps {
  /** Published config on the public page, draft config in the Settings preview. */
  config: ShopConfig;
}
/**
 * THE merchant storefront. This single component renders the public shop at
 * talab.app/shop/<handle> and the Business Hub live preview — the only thing
 * that differs between them is which config it is handed and the viewport it
 * is mounted in. There is deliberately no separate "preview" markup.
 */
export function MerchantStorefront({
  config
}: MerchantStorefrontProps) {
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  // The shopper's account. One global Talab identity, but everything they see
  // here is scoped to THIS merchant.
  const [session, setSession] = useState<ShopSession | null>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [myOrders, setMyOrders] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const theme = useMemo(() => resolveTheme(config.theme), [config.theme]);
  const vars = useMemo(() => themeCssVars(config.theme), [config.theme]);
  const categories = useMemo(() => storefrontCategories(config), [config]);
  const featured = useMemo(() => storefrontFeatured(config), [config]);
  const emphasis = config.hero.emphasis;
  const emphasisWeight = config.hero.emphasisWeight;
  const {
    leading,
    last,
    brandTokens
  } = splitHeadline(config.hero.headline, config.publicName);
  useEffect(() => {
    if (chat.length > 0) bottomRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chat, isTyping]);
  const reply = (vehicle?: Vehicle) => {
    setIsTyping(true);
    window.setTimeout(() => {
      setIsTyping(false);
      const content = vehicle ? `The ${vehicle.name} is ${vehicle.monthly} (${vehicle.daily}) — ${vehicle.details.toLowerCase()}. It includes comprehensive insurance, scheduled maintenance and 24/7 roadside support, with delivery and collection anywhere in Dubai or Abu Dhabi. Share your dates and I'll confirm availability and reserve it for you.` : `${config.legalName || config.publicName} covers short-term rentals and long-term leasing across the UAE — sedans, SUVs and 4WDs, luxury, vans, pickups, buses and electric or hybrid options. Every vehicle includes maintenance and 24/7 roadside support. Tell me your dates, budget and whether this is for personal or corporate use, and I'll shortlist the best matches from our fleet.`;
      setChat(prev => [...prev, {
        role: 'ai',
        content
      }]);
    }, 1100);
  };
  const ask = (text: string, vehicle?: Vehicle) => {
    if (!text.trim()) return;
    setChat(prev => [
    // The merchant's own greeting opens every conversation.
    ...(prev.length === 0 && config.ai.greeting ? [{
      role: 'ai' as const,
      content: config.ai.greeting
    }] : []), ...prev, {
      role: 'user' as const,
      content: text,
      vehicle
    }]);
    setQuery('');
    reply(vehicle);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    ask(query);
  };
  const chooseCategory = (label: string, prompt: string) => {
    setActiveCategory(label);
    ask(prompt);
  };
  // Always a way out of a conversation, back to the storefront.
  const backToStorefront = () => {
    setChat([]);
    setActiveCategory(null);
    setQuery('');
    setIsTyping(false);
    setMyOrders(false);
  };
  // Scoped by merchant AND by shopper — a merchant's storefront never shows
  // orders placed with any other shop.
  const myOrderList = useMemo(() => session ? shopperOrders : [], [session]);
  const inThread = chat.length > 0;
  const highlights = config.content.highlights;
  // Cards keep a constant width and the row centres itself, so removing one
  // never leaves a lopsided, left-hugging row.
  const highlightWidth = highlights.length >= 5 ? 'lg:w-[calc(20%-9.6px)]' : 'lg:w-[calc(25%-9px)]';
  const merchantAvatar = <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden" data-id="element-4497" data-mp="1mwoydneg1fkz">
      {config.logo ? <img src={config.logo} alt={config.publicName} className="w-6 h-auto object-contain" data-id="element-4498" data-mp="bh0g39c4x4qi" /> : <span className="text-xs font-bold text-[var(--mp)]" data-id="element-4499" data-mp="2zm5zfryt8w5j">
          {config.publicName.charAt(0)}
        </span>}
    </div>;
  return <div style={{
    ...vars,
    backgroundColor: '#FFFFFF'
  }} className="app-fill-screen flex-1 flex flex-col min-h-screen relative" data-id="element-4500" data-mp="2porilr8z65cp">
      <ShopBackdrop background={theme.pageBackground} variant={config.theme.backdrop} strength={config.theme.backgroundStrength / 100} data-id="element-4501" data-mp="2g09kjhs2yccr" />

      <div className="relative z-20 flex-shrink-0" data-id="element-4502" data-mp="2ksuc0cnikdd4">
        <ShopTopBar config={config} onHome={inThread || myOrders ? backToStorefront : undefined} account={<ShopAccountControl session={session} dark={theme.darkSurface} onSignIn={() => setSignInOpen(true)} onMyOrders={() => {
        backToStorefront();
        setMyOrders(true);
      }} onSignOut={() => {
        setSession(null);
        setMyOrders(false);
      }} data-id="element-4504" data-mp="2byo4jnk91hyc" />} data-id="element-4503" data-mp="2704fvfe9ts2a" />
      </div>

      {myOrders ?
    // --- The shopper's orders with THIS merchant only --------------------
    <ShopMyOrders config={config} orders={myOrderList} onBack={() => setMyOrders(false)} onMessage={text => {
      setMyOrders(false);
      ask(text);
    }} data-id="element-4505" data-mp="1ndt5vbvkscqs" /> : inThread ?
    // --- Conversation view ------------------------------------------------
    <div className="relative z-10 flex-1 flex flex-col min-h-0" data-id="element-4506" data-mp="2b1sv27d8nv39">
          <div className="flex-shrink-0 px-4 lg:px-12 pt-4 pb-3" data-id="element-4507" data-mp="263izhvnlqsh4">
            <div className="max-w-3xl mx-auto w-full flex items-center gap-2" data-id="element-4508" data-mp="266dj5kysk99h">
              <button onClick={backToStorefront} className="inline-flex items-center gap-2 pl-3 pr-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-[var(--mp-deep)] shadow-sm hover:border-[var(--mp-50)] hover:bg-[var(--mp-04)] transition-colors duration-150 ease-out" data-id="element-4509" data-mp="2l1jnm3i8o00p">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-4510" data-mp="1aniw56fv8dxm" />
                Back to {config.publicName}
              </button>
              <button onClick={backToStorefront} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-500 shadow-sm hover:border-[var(--mp-50)] hover:text-[var(--mp-d)] transition-colors duration-150 ease-out" data-id="element-4511" data-mp="1fm3vz4r7gvlj">
                <PlusIcon className="w-4 h-4" data-id="element-4512" data-mp="rvhlz7xn518w" />
                <span className="hidden sm:inline" data-id="element-4513" data-mp="woj5jt8sloyt">New conversation</span>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 lg:px-12 pb-8" data-id="element-4514" data-mp="3vdrqyumnse1p">
            <div className="max-w-3xl mx-auto w-full pb-6 space-y-5" data-id="element-4515" data-mp="476tas23m7vp">
              {chat.map((m, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-4516" data-mp="2hh88awveytwo" data-mpi={i}>
                  {m.role === 'ai' && merchantAvatar}
                  <div className="max-w-[80%] space-y-2" data-id="element-4517" data-mp="1pr3i2hkz3cnj">
                    {m.vehicle && <div className="flex items-center gap-3 p-2 bg-white border border-slate-100 rounded-2xl shadow-sm" data-id="element-4518" data-mp="1krz99s1yngxq">
                        <img src={m.vehicle.image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" data-id="element-4519" data-mp="1ftfjtho5rn0m" />
                        <div className="min-w-0 pr-1" data-id="element-4520" data-mp="2y8iyf30bpymy">
                          <p className="text-xs font-bold text-[var(--mp-deep)] truncate" data-id="element-4521" data-mp="2taqutbqbgo2x">
                            {m.vehicle.name}
                          </p>
                          <p className="text-[11px] font-semibold text-[var(--mp)]" data-id="element-4522" data-mp="371cev3rib42e">
                            {m.vehicle.monthly}
                          </p>
                        </div>
                      </div>}
                    <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'rounded-br-md' : 'bg-white border border-slate-100 text-slate-700 shadow-sm rounded-bl-md'}`} style={m.role === 'user' ? {
                background: 'var(--mp)',
                color: 'var(--mp-t)'
              } : undefined} data-id="element-4523" data-mp="2ac4uhz2mj102">
                      {m.content}
                    </div>
                  </div>
                </motion.div>)}

              {isTyping && <div className="flex justify-start" data-id="element-4524" data-mp="3tb2qgikh1wpe">
                  {merchantAvatar}
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1" data-id="element-4525" data-mp="23zz9r4mvqf6">
                    {[0, 1, 2].map(d => <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-[var(--mp-40)]" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: d * 0.2
              }} data-id="element-4526" data-mp="pw6s4gb5hf9u" />)}
                  </div>
                </div>}
              <div ref={bottomRef} data-id="element-4527" data-mp="1ne2l1f9krcsr" />
            </div>
          </div>

          <div className="flex-shrink-0 px-4 lg:px-12 pb-6 pt-2 relative z-20" style={{
        background: 'linear-gradient(to top, #FFFFFF 0%, #FFFFFF 60%, transparent 100%)'
      }} data-id="element-4528" data-mp="1ifsph3jxua6m">
            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto w-full bg-white rounded-2xl shadow-[0_8px_30px_var(--mp-card-shadow)] border border-slate-100 p-2 flex items-end gap-2" data-id="element-4529" data-mp="19fcoq1ukxh5g">
              <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }} placeholder={`Ask ${config.publicName} anything...`} className="flex-1 resize-none outline-none text-base text-slate-800 placeholder:text-slate-300 bg-transparent px-3 py-2.5 min-h-[44px]" rows={1} data-id="element-4530" data-mp="uqgi89868c6y" />
              <button type="submit" disabled={!query.trim()} aria-label="Send" className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-[opacity] duration-150 ease-out flex-shrink-0 shadow-sm" style={{
            background: 'var(--m-chat)',
            color: 'var(--m-chat-t)'
          }} data-id="element-4531" data-mp="2xf80wsdvmwtn">
                <ArrowUpIcon className="w-5 h-5" data-id="element-4532" data-mp="2imecuikjh8dd" />
              </button>
            </form>
          </div>
        </div> :
    // --- Storefront view --------------------------------------------------
    <div className="relative z-10 flex-1 overflow-y-auto" data-id="element-4533" data-mp="1uywpr0c7yygq">
          <div className="px-4 lg:px-12 pt-8 lg:pt-12 pb-16 flex flex-col items-center" data-id="element-4534" data-mp="1uxbrazmsavdl">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="w-full flex flex-col items-center" data-id="element-4535" data-mp="1gz1aauvjigp">
              {/* Merchant brand lockup */}
              {config.hero.style !== 'minimal' && <div className="relative mb-7" data-id="element-4536" data-mp="f72k0rujou1e">
                  <div className="absolute -inset-4 border border-[var(--mp-14)] rounded-3xl" data-id="element-4537" data-mp="t73ki66wdzgc" />
                  <div className="absolute -inset-9 border border-[var(--mp-07)] rounded-[2rem]" data-id="element-4538" data-mp="p5s1wkmfyehn" />
                  <div className="relative z-10 bg-white rounded-2xl border border-slate-100 shadow-[0_10px_34px_var(--mp-card-shadow)] px-7 py-5" data-id="element-4539" data-mp="2uyqhcodh0okq">
                    {config.logo ? <img src={config.logo} alt={config.legalName || config.publicName} className="h-9 lg:h-11 w-auto object-contain" data-id="element-4540" data-mp="2qzhn7ieiqoon" /> : <span className="font-display font-bold text-2xl lg:text-3xl text-[var(--mp-deep)]" data-id="element-4541" data-mp="272lrbx9ayt2l">
                        {config.publicName}
                      </span>}
                  </div>
                </div>}

              {/* Headline */}
              <div className="text-center space-y-3 mb-7" data-id="element-4542" data-mp="225d2xexk9ssw">
                {config.hero.logoDisplay === 'logo-name' && <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400" data-id="element-4543" data-mp="1o6dhsh99bw1h">
                    {config.publicName}
                  </p>}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-500 shadow-sm" data-id="element-4544" data-mp="3ijv7oax4x4zo">
                  <CheckCircle2Icon className="w-3.5 h-3.5 text-[var(--mp)]" data-id="element-4545" data-mp="3l3hrqwcjfwzk" />
                  talab.app/shop/{config.handle}
                </div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[var(--mp-deep)]" data-id="element-4546" data-mp="3h03ay3grzcl2">
                  {leading.map((word, i) => <React.Fragment key={`${word}-${i}`} data-id="element-4547" data-mp="374rvfixjm1c9" data-mpi={i}>
                      {brandTokens.includes(word) ? <span className="text-[var(--mp)]" data-id="element-4548" data-mp="8krxp57ixflf">{word}</span> : word}{' '}
                    </React.Fragment>)}
                  {last && <span className="relative inline-block" data-id="element-4549" data-mp="3q2kxyk498994">
                      <span className="relative z-10" style={emphasis === 'colour' ? {
                  color: 'var(--ma)'
                } : undefined} data-id="element-4550" data-mp="3pzyqsll9zvrf">
                        {last}
                      </span>
                      {/* Accent emphasis, drawn once on load */}
                      {(emphasis === 'underline' || emphasis === 'highlight') && <motion.span initial={{
                  scaleX: 0
                }} animate={{
                  scaleX: 1
                }} transition={{
                  duration: 0.5,
                  delay: 0.35,
                  ease: [0.23, 1, 0.32, 1]
                }} style={{
                  originX: 0,
                  background: 'var(--ma-70)',
                  height: emphasis === 'highlight' ? `${42 + emphasisWeight * 0.5}%` : `${3 + emphasisWeight * 0.12}px`,
                  bottom: emphasis === 'highlight' ? '4%' : '0.15em',
                  borderRadius: emphasis === 'highlight' ? '0.25rem' : '999px'
                }} className="absolute left-0 right-0" data-id="element-4551" data-mp="18roqwb9h80w2" />}
                    </span>}
                </h1>
                <p className="text-slate-500 text-sm lg:text-base max-w-2xl mx-auto leading-relaxed" data-id="element-4552" data-mp="2cv7mmffcylty">
                  {config.hero.subtitle}
                </p>
                <div className="flex flex-col items-center gap-1 pt-1" data-id="element-4553" data-mp="282wpfkc05e53">
                  <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400" data-id="element-4554" data-mp="1ke551f4nm02v">
                    <SparklesIcon className="w-3.5 h-3.5 text-[var(--mp)]" data-id="element-4555" data-mp="1ffk237y0p2aq" />
                    Powered by{' '}
                    <span className="text-[var(--mp-d)]" data-id="element-4556" data-mp="3r1cf0edh4f8s">Talab.app</span>
                  </p>
                  <p className="text-[10px] tracking-[0.1em] uppercase text-slate-300" data-id="element-4557" data-mp="3vvytiqeuqnx3">
                    AI conversational ecommerce
                  </p>
                </div>
              </div>

              {/* Category strip */}
              {categories.length > 0 && <div className="w-full max-w-5xl mb-6" data-id="element-4558" data-mp="dosj7ef5wc7d">
                  <ShopCategoryStrip categories={categories} active={activeCategory} onSelect={chooseCategory} data-id="element-4559" data-mp="invo8yv56ifc" />
                </div>}

              {/* Chat composer — the focal point of the page */}
              <div className="w-full max-w-3xl rounded-3xl shadow-[0_8px_30px_var(--mp-card-shadow)] border border-slate-100 p-2 mb-8 relative group transition-shadow duration-200 ease-out z-10" style={{
            background: 'var(--m-chat-surface)'
          }} data-id="element-4560" data-mp="2pdabu7qminmc">
                <div className="absolute -inset-0.5 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" style={{
              background: 'linear-gradient(to right, var(--mp-25), var(--ma-30))'
            }} data-id="element-4561" data-mp="39kbzlqiuyneh" />
                <form onSubmit={handleSubmit} className="relative rounded-[22px] p-5 flex flex-col gap-3" style={{
              background: 'var(--m-chat-surface)'
            }} data-id="element-4562" data-mp="2ltpvgabz5n9r">
                  <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }} placeholder={`Ask ${config.publicName} anything...`} className="w-full resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[44px]" rows={1} data-id="element-4563" data-mp="2gxhm5dkgkoai" />
                  <div className="flex items-center justify-between mt-2" data-id="element-4564" data-mp="278kbfwvk1h43">
                    <div className="hidden sm:flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.12em]" data-id="element-4565" data-mp="22a0rg20iufed">
                      {QUICK_INTENTS.map((intent, i) => <React.Fragment key={intent} data-id="element-4566" data-mp="2g3icjr8uw06i" data-mpi={i}>
                          {i > 0 && <span className="w-1 h-1 rounded-full bg-[var(--m-chat-accent)]" data-id="element-4567" data-mp="2c1x0g845bpxd" />}
                          <button type="button" onClick={() => setQuery(`${intent} `)} className="text-[var(--mp-deep-60)] hover:text-[var(--mp)] transition-colors duration-150 ease-out" data-id="element-4568" data-mp="2l0id5mmxsaq8">
                            {intent}
                          </button>
                        </React.Fragment>)}
                    </div>

                    <div className="flex items-center gap-1 ml-auto" data-id="element-4569" data-mp="1wfscjh0w2nkm">
                      <button type="button" onClick={() => setIsRecording(r => !r)} aria-label="Voice input" className={`p-2.5 rounded-full transition-colors duration-150 ease-out ${isRecording ? 'text-white bg-error' : 'text-slate-400 hover:text-[var(--mp)] hover:bg-slate-50'}`} data-id="element-4570" data-mp="21cis838s7h5i">
                        <MicIcon className="w-5 h-5" data-id="element-4571" data-mp="g1am7q22bdxd" />
                      </button>
                      <button type="button" aria-label="Attach image" className="p-2.5 text-slate-400 hover:text-[var(--mp)] hover:bg-slate-50 rounded-full transition-colors duration-150 ease-out" data-id="element-4572" data-mp="kyivf158g7mh">
                        <ImageIcon className="w-5 h-5" data-id="element-4573" data-mp="3pgimu6qn049" />
                      </button>
                      <button type="button" aria-label="Attach file" className="p-2.5 text-slate-400 hover:text-[var(--mp)] hover:bg-slate-50 rounded-full transition-colors duration-150 ease-out" data-id="element-4574" data-mp="3uwkrlumg0wfb">
                        <PaperclipIcon className="w-5 h-5" data-id="element-4575" data-mp="mg7qtsezokh2" />
                      </button>
                      <button type="submit" disabled={!query.trim()} aria-label="Send" className="ml-2 p-3.5 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-[opacity] duration-150 ease-out shadow-[0_4px_14px_var(--mp-shadow)]" style={{
                    background: 'var(--m-chat)',
                    color: 'var(--m-chat-t)'
                  }} data-id="element-4576" data-mp="iewayelx3dm2">
                        <ArrowUpIcon className="w-5 h-5" data-id="element-4577" data-mp="kzkbmc4e644q" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Suggested questions */}
              {config.content.questions.length > 0 && <div className="w-full max-w-5xl mb-14" data-id="element-4578" data-mp="1im9f6qeqsosp">
                  <div className="flex items-center justify-center gap-4 mb-5" data-id="element-4579" data-mp="1ijn80rvrkbb0">
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-4580" data-mp="14ob53x7gfej0" />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-4581" data-mp="18mipd214mxyy">
                      Try asking something like
                    </p>
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-4582" data-mp="3teazoelr553r" />
                  </div>
                  <div className="flex flex-wrap justify-center gap-3" data-id="element-4583" data-mp="3ltqlci82dsm0">
                    {config.content.questions.map((prompt, index) => <motion.button key={prompt.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15 + index * 0.04,
                duration: 0.25,
                ease: 'easeOut'
              }} onClick={() => ask(prompt.text)} className="w-full sm:w-[calc(50%-6px)] lg:w-[calc(25%-9px)] flex items-center justify-between gap-2 px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-[var(--mp-30)] hover:bg-[var(--mp-03)] transition-colors duration-150 ease-out text-left group shadow-sm" data-id="element-4584" data-mp="3gxjf0obraf3j" data-mpi={index}>
                        <span className="text-[13px] font-medium text-slate-600 group-hover:text-[var(--mp-deep)] transition-colors duration-150 ease-out" data-id="element-4585" data-mp="3946u5eg53a0v">
                          {prompt.text}
                        </span>
                        <ArrowRightIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[var(--mp)] transition-colors duration-150 ease-out flex-shrink-0" data-id="element-4586" data-mp="3iu9obhyzyu0s" />
                      </motion.button>)}
                  </div>
                </div>}

              {/* Shop highlights */}
              {highlights.length > 0 && <div className="w-full max-w-6xl" data-id="element-4587" data-mp="303ihoo6e9hq3">
                  <div className="flex flex-wrap justify-center gap-3" data-id="element-4588" data-mp="304bfgzndkww3">
                    {highlights.map(h => {
                const Icon = HIGHLIGHT_ICONS[h.icon] ?? ShieldCheckIcon;
                return <div key={h.id} className={`w-full sm:w-[calc(50%-6px)] ${highlightWidth} flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-100 shadow-sm`} data-id="element-4589" data-mp="ffdyk43qrkwu">
                          <span className="w-9 h-9 rounded-xl bg-[var(--mp-07)] flex items-center justify-center flex-shrink-0" data-id="element-4590" data-mp="1q4h63u3wkyq0">
                            <Icon className="w-[18px] h-[18px] text-[var(--mp)]" strokeWidth={1.75} data-id="element-4591" data-mp="1l4cxeq98l8ac" />
                          </span>
                          <div className="min-w-0" data-id="element-4592" data-mp="176ocjif6615a">
                            <p className="text-[13px] font-bold text-[var(--mp-deep)] leading-tight" data-id="element-4593" data-mp="177fxlzb54ghb">
                              {h.title}
                            </p>
                            {h.detail && <p className="text-[11px] text-slate-400 mt-1 leading-snug" data-id="element-4594" data-mp="31jw9pgqmaou3">
                                {h.detail}
                              </p>}
                          </div>
                        </div>;
              })}
                  </div>
                </div>}

              {/* Featured vehicles — supports the conversation, never competes */}
              {featured.length > 0 && <section className="w-full max-w-6xl pt-16" data-id="element-4595" data-mp="20nz2viyby5ay">
                  <div className="text-center mb-8" data-id="element-4596" data-mp="1mp7nlt7qedl8">
                    <h2 className="font-display font-bold text-2xl lg:text-3xl text-[var(--mp-deep)] tracking-tight" data-id="element-4597" data-mp="1mrtx3ye6n363">
                      Featured Vehicles
                    </h2>
                    <p className="text-sm text-slate-500 mt-1.5" data-id="element-4598" data-mp="1dqnenjrz3ia7">
                      Popular options available from {config.publicName}
                    </p>
                    <span className="inline-block w-10 h-[3px] rounded-full bg-[var(--ma)] mt-4" data-id="element-4599" data-mp="382u0rdmenjha" />
                  </div>
                  <div className="flex flex-wrap justify-center gap-5" data-id="element-4600" data-mp="3c3dz2kipst50">
                    {featured.map(vehicle => <div key={vehicle.id} className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(25%-15px)] flex" data-id="element-4601" data-mp="10hllrczcje6f">
                        <FeaturedCarCard vehicle={vehicle} onAsk={v => ask(`Tell me more about the ${v.name}`, v)} onCheckAvailability={v => ask(`Is the ${v.name} available this week?`, v)} onCompare={v => ask(`Compare the ${v.name} with your other ${v.category.toLowerCase()}`, v)} data-id="element-4602" data-mp="1a8puvw2cc4tc" />
                      </div>)}
                  </div>
                </section>}

              {/* Browse by category — structured path back into the chat */}
              {categories.length > 0 && <section className="w-full max-w-5xl pt-16" data-id="element-4603" data-mp="2wc6el80ck5uz">
                  <div className="text-center mb-7" data-id="element-4604" data-mp="2rjwmblbretpj">
                    <h2 className="font-display font-bold text-xl lg:text-2xl text-[var(--mp-deep)] tracking-tight" data-id="element-4605" data-mp="2ribpfp1z2yoi">
                      Browse by Category
                    </h2>
                    <p className="text-sm text-slate-500 mt-1.5" data-id="element-4606" data-mp="2hntf4121w4vq">
                      Pick a starting point and continue the conversation
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3" data-id="element-4607" data-mp="28mc2wtrrkncb">
                    {categories.map(c => {
                const Icon = CATEGORY_ICONS[c.icon];
                return <button key={c.name} onClick={() => chooseCategory(c.label, c.prompt)} className="group inline-flex items-center gap-2.5 pl-4 pr-5 py-3 bg-white border border-slate-100 rounded-2xl shadow-sm hover:border-[var(--mp-40)] hover:shadow-md transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-4608" data-mp="6tnve8837dqq">
                          <Icon className="w-4 h-4 text-slate-400 group-hover:text-[var(--mp)] transition-colors duration-150 ease-out" strokeWidth={1.5} data-id="element-4609" data-mp="o3ru5ep4ys5g" />
                          <span className="text-sm font-medium text-slate-600 group-hover:text-[var(--mp-deep)] transition-colors duration-150 ease-out" data-id="element-4610" data-mp="3rlji8i05iryd">
                            {c.name}
                          </span>
                        </button>;
              })}
                  </div>
                </section>}
            </motion.div>
          </div>

          <footer className="relative z-10 py-6 text-center border-t border-[var(--mp-10)]" data-id="element-4611" data-mp="1gre02tlu68vh">
            <p className="text-xs text-slate-400" data-id="element-4612" data-mp="3cuinly5ts508">
              {config.legalName || config.publicName}
            </p>
            <a href="https://talab.app" className="inline-flex items-center gap-1.5 mt-2 text-xs text-slate-400 hover:text-[var(--mp-d)] transition-colors duration-150 ease-out" data-id="element-4613" data-mp="37vz03tbl2l5r">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-4614" data-mp="3hsk54enlrw9l" />
              Powered by{' '}
              <span className="font-semibold text-slate-500" data-id="element-4615" data-mp="2u5c1e15tov9o">Talab.app</span>
              <span className="hidden sm:inline text-slate-300" data-id="element-4616" data-mp="2p728jx7km6r8">
                · AI conversational ecommerce
              </span>
            </a>
          </footer>
        </div>}

      {signInOpen && <ShopSignInSheet config={config} onClose={() => setSignInOpen(false)} onSignedIn={s => {
      setSession(s);
      setSignInOpen(false);
    }} data-id="element-4617" data-mp="2y54dgnufdjjs" />}
    </div>;
}